import React, { useState, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import { DEFAULT_THEME_COLORS } from '../../../../css/theme'
import styled from '@emotion/styled'
import useFlow from 'modules/flow/state/use-flow'
import FlowOneColumnStepLayout from '../one-column-step-layout/one-column-step-layout'
import ScrollableList from '../../../ui/molecules/ScrollableList'
import useConfig from '../../../config/state/use-config'
import useOrder from '../../../order/state/order/use-order'
import useDesignationSearch from '../../../order/components/designation/use-designation-search'
import Row from '../../../ui/atoms/Layout/Row'
import {
  trackFormSubmitEvent,
  trackFormErrorEvent,
  trackFormSkipEvent,
} from '../../../analytics/helpers/track-form-events'

const ProgramDesignationStep = () => {
  const { t } = useTranslation()
  const flow = useFlow()
  const config = useConfig()
  const order = useOrder()

  const primaryColor =
    config.theme['--theme-color__brand-primary'] ||
    DEFAULT_THEME_COLORS['--theme-color--neutral-10']

  const [query, setQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const lastTrackedErrorRef = useRef<string | null>(null) // Ref to track last error

  const [selectedDesignationId, setSelectedDesignationId] = useState<number | null>(
    order.designation.value.designationId,
  )

  const [selectedDesignationName, setSelectedDesignationName] = useState(
    order.designation.value.designationName,
  )

  const [isSearchActive, setIsSearchActive] = useState(
    selectedDesignationName === '' ? false : true,
  )

  const { data, error, hasMore, loading, designations } = useDesignationSearch(
    config.campaignId,
    query,
    pageNumber,
  )

  const goNext = () => {
    const nextStep = flow.nextStep

    // Ensure the user has selected a designation before continuing
    if (!selectedDesignationId || !selectedDesignationName) {
      const currentError = 'No designation selected'

      // Track error if it's different from the last tracked error
      if (lastTrackedErrorRef.current !== currentError) {
        trackFormErrorEvent(flow.currentStep.name, currentError)

        lastTrackedErrorRef.current = currentError
      }
    }

    if (nextStep) {
      flow.goToStep(nextStep.name)
    }
  }

  const onSkip = () => {
    trackFormSkipEvent(flow.currentStep.name)

    goNext()
  }

  const onSelect = ({ value: designationId }: { value: number }) => {
    const designation = designations?.find((d) => d.id === designationId)

    setIsSearchActive(false)

    if (designation) {
      setSelectedDesignationId(designationId)
      setSelectedDesignationName(designation.name)
    }
  }

  const onContinue = () => {
    const designation = designations?.find((d) => d.id === selectedDesignationId)

    if (designation) {
      const designationPayload = {
        designationId: designation.id,
        designationName: designation.name,
      }

      order.designation.setValue(designationPayload)
      order.designation.save(designationPayload)

      // If a designation is selected, track the submission and continue
      trackFormSubmitEvent(flow.currentStep.name, {
        designation_match: selectedDesignationId,
        name: selectedDesignationName,
      })
    }
  }

  const onFetch = (query: string, pageNumber: number) => {
    setQuery(query)
    setPageNumber(pageNumber)
  }

  const resetSearch = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    setSelectedDesignationId(null)
    setSelectedDesignationName('')
  }

  return (
    <>
      <FlowOneColumnStepLayout.Main
        title={flow.currentStep.title}
        backButtonDataId="designation-step-back-button"
      >
        <Wrapper>
          {!selectedDesignationName && (
            <Row style={{ gap: '8px' }}>
              <ScrollableList
                height={200}
                data={data}
                error={error}
                loading={loading}
                hasMore={hasMore}
                onSelect={onSelect}
                fetchNewData={onFetch}
                selected={selectedDesignationId ? selectedDesignationId : ''}
                isActivatedSearch={isSearchActive}
                dataId="designation-step"
              />
            </Row>
          )}

          {selectedDesignationName && (
            <Row style={{ flexDirection: 'column', gap: '8px' }}>
              <Row>
                {t('ProgramDesignationStep.selectedDesignation.label', 'Designation selected:')}
              </Row>
              <Row style={{ fontWeight: '700' }}>{selectedDesignationName}</Row>
              <Row style={{ fontSize: '12px' }}>
                <a
                  href={''}
                  onClick={resetSearch}
                  data-id="designation-step-choose-another-designation"
                >
                  {t(
                    'ProgramDesignationStep.selectedDesignation.chooseAnotherDesignation',
                    'Choose another designation ',
                  )}
                </a>
              </Row>
            </Row>
          )}
        </Wrapper>
      </FlowOneColumnStepLayout.Main>
      <FlowOneColumnStepLayout.Footer>
        <FlowOneColumnStepLayout.FooterButton
          size="lg"
          dataId="designation-step-skip-button"
          color={DEFAULT_THEME_COLORS['--theme-color--neutral-100']}
          borderColor={DEFAULT_THEME_COLORS['--theme-color--neutral-80']}
          onClick={onSkip}
        >
          {t('ProgramDesignationStep.buttons.skip', 'Skip')}
        </FlowOneColumnStepLayout.FooterButton>
        <FlowOneColumnStepLayout.FooterButton
          size="lg"
          dataId="designation-step-continue-button"
          color={primaryColor}
          onClick={() => {
            onContinue()
            goNext()
          }}
        >
          {t('ProgramDesignationStep.buttons.continue', 'Continue')}
        </FlowOneColumnStepLayout.FooterButton>
      </FlowOneColumnStepLayout.Footer>
    </>
  )
}

export default ProgramDesignationStep

const Wrapper = styled.div`
  width: 500px;
  @media (max-width: 767px) {
    text-align: center;
    max-width: 345px;
    div {
      align-items: center;
    }
  }
`
